import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Public escalation-ACK page. Sprint 3 item 3.2 (§29.5).
 *
 * Reached from email/SMS escalation notices: the URL embeds a single
 * unguessable token that IS the credential. No login required — the
 * token is the auth. Idempotent: a second click on the same link just
 * shows "already acknowledged".
 *
 * Token shape: secrets.token_urlsafe(32) → 43-char URL-safe slug.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { publicGet, publicPost } from '@/lib/api';
export function EscalationAckPage() {
    const { token } = useParams();
    const preview = useQuery({
        queryKey: ['escalation-ack', token],
        queryFn: () => publicGet(`/escalations/ack/${token}`),
        enabled: !!token,
        retry: false,
    });
    const ack = useMutation({
        mutationFn: () => publicPost(`/escalations/ack/${token}`, {}),
    });
    if (!token) {
        return _jsx(Shell, { children: _jsx(Error, { message: "Missing acknowledgement token." }) });
    }
    if (preview.isLoading) {
        return _jsx(Shell, { children: _jsx("p", { className: "text-sm text-slate-500", children: "Loading\u2026" }) });
    }
    if (preview.isError) {
        return (_jsx(Shell, { children: _jsx(Error, { message: "This acknowledgement link is invalid or has expired. If you received it in an alert and still need to acknowledge, sign in to the agent console and acknowledge the escalation from the ticket page." }) }));
    }
    const data = preview.data;
    if (!data)
        return null;
    // Treat both "preview said already" and "POST returned already" as
    // the same terminal state.
    const alreadyDone = data.already_acknowledged || ack.data?.already_acknowledged;
    const ackedAt = ack.data?.acknowledged_at ?? data.acknowledged_at;
    const justAcked = ack.isSuccess && !alreadyDone;
    if (alreadyDone || justAcked) {
        return (_jsx(Shell, { children: _jsxs("div", { className: "text-center", children: [_jsx(CheckCircle2, { className: "mx-auto h-12 w-12 text-emerald-500" }), _jsx("h1", { className: "mt-3 text-2xl font-semibold", children: justAcked ? 'Thanks — acknowledged.' : 'Already acknowledged.' }), _jsxs("p", { className: "mt-2 text-sm text-slate-500", children: ["Ticket ", _jsx("span", { className: "font-mono text-xs", children: data.ticket_id.slice(0, 8) }), ' ', "escalation acknowledged", ' ', ackedAt ? `at ${new Date(ackedAt).toLocaleString()}` : '', "."] }), _jsx("p", { className: "mt-3 text-xs text-slate-400", children: "The on-call rotation has been notified. You can close this page." })] }) }));
    }
    return (_jsx(Shell, { children: _jsxs("div", { className: "text-center", children: [_jsx(ShieldAlert, { className: "mx-auto h-12 w-12 text-amber-500" }), _jsx("h1", { className: "mt-3 text-2xl font-semibold", children: "Acknowledge escalation" }), _jsxs("p", { className: "mt-2 text-sm text-slate-500", children: ["An escalation fired on ticket", ' ', _jsx("span", { className: "font-mono text-xs", children: data.ticket_id.slice(0, 8) }), " at", ' ', new Date(data.fired_at).toLocaleString(), ". Clicking the button below tells the system you've seen the alert and are taking it from here."] }), ack.isError && (_jsx("div", { className: "mt-3 rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-700", children: "Couldn't record the acknowledgement. Please try again or sign in to acknowledge directly." })), _jsx(Button, { className: "mt-5", onClick: () => ack.mutate(), disabled: ack.isPending, children: ack.isPending ? 'Acknowledging…' : 'I acknowledge' }), _jsx("p", { className: "mt-4 text-xs text-slate-400", children: "This link works only once and is bound to this single escalation." })] }) }));
}
function Shell({ children }) {
    return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950", children: _jsx(Card, { className: "w-full max-w-md", children: _jsx(CardBody, { className: "p-8", children: children }) }) }));
}
function Error({ message }) {
    return (_jsxs("div", { className: "text-center", children: [_jsx(ShieldAlert, { className: "mx-auto h-10 w-10 text-rose-500" }), _jsx("div", { className: "mt-3 text-lg font-semibold", children: "Link unavailable" }), _jsx("p", { className: "mt-2 text-sm text-slate-500", children: message })] }));
}
